import { api } from './api/client';
import { store } from './state/store';
import { ZoomLevel } from './types';
import { initGrid, renderGrid } from './components/grid';
import { renderErasLegend } from './components/legend';
import { initWeekModal } from './components/modal';
import { initEraModal } from './components/eras';
import { initAdminControls, renderAdminHeaderControls } from './components/admin';

// Theme management
function initTheme(): void {
  const themeToggleBtn = document.getElementById('theme-toggle-btn');
  const themeIcon = document.getElementById('theme-icon');

  const savedTheme = localStorage.getItem('lifecal_theme') || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
  document.documentElement.setAttribute('data-theme', savedTheme);
  if (themeIcon) themeIcon.textContent = savedTheme === 'dark' ? '☀️' : '🌙';

  themeToggleBtn?.addEventListener('click', () => {
    const current = document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
    const next = current === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    localStorage.setItem('lifecal_theme', next);
    if (themeIcon) themeIcon.textContent = next === 'dark' ? '☀️' : '🌙';
  });
}

// Zoom management
function initZoom(): void {
  const zoomSelect = document.getElementById('zoom-select') as HTMLSelectElement;
  const grid = document.getElementById('calendar-grid');

  const savedZoom = (localStorage.getItem('lifecal_zoom') as ZoomLevel) || 'normal';
  if (zoomSelect) zoomSelect.value = savedZoom;
  if (grid) grid.setAttribute('data-zoom', savedZoom);

  zoomSelect?.addEventListener('change', () => {
    const zoom = zoomSelect.value as ZoomLevel;
    if (grid) grid.setAttribute('data-zoom', zoom);
    localStorage.setItem('lifecal_zoom', zoom);
  });
}

// Header and statistics update
function updateHeaderInfo(): void {
  const titleEl = document.getElementById('header-title');
  const bioEl = document.getElementById('header-bio');
  const statsEl = document.getElementById('header-stats');

  if (titleEl) titleEl.textContent = store.meta.title;
  if (bioEl) bioEl.textContent = store.meta.bio;

  if (statsEl) {
    const totalWeeks = store.getTotalWeeks();
    const passedWeeks = Math.max(0, store.currentWeekIndex);
    const leftWeeks = Math.max(0, totalWeeks - passedWeeks);
    const ageYears = Math.floor(passedWeeks / 52);
    const percentLived = Math.min(100, Math.round((passedWeeks / totalWeeks) * 100));

    statsEl.innerHTML = `
      <div class="stat-item">
        <span class="stat-value">${ageYears} yrs</span>
        <span class="stat-label">Current Age</span>
      </div>
      <div class="stat-item">
        <span class="stat-value">${passedWeeks.toLocaleString()}</span>
        <span class="stat-label">Weeks Passed</span>
      </div>
      <div class="stat-item">
        <span class="stat-value">${leftWeeks.toLocaleString()}</span>
        <span class="stat-label">Weeks Left</span>
      </div>
      <div class="stat-item">
        <span class="stat-value">${percentLived}%</span>
        <span class="stat-label">Progress</span>
      </div>
    `;
  }
}

// Full app render
function renderApp(): void {
  updateHeaderInfo();
  renderAdminHeaderControls();
  renderErasLegend();
  renderGrid();
}

async function bootstrap(): Promise<void> {
  initTheme();
  initZoom();
  initGrid();
  initWeekModal();
  initEraModal();
  initAdminControls();

  store.subscribe(() => {
    renderApp();
  });

  try {
    const [meta, notes] = await Promise.all([api.getMeta(), api.getWeeks()]);
    store.setMeta(meta);
    store.setNotes(notes);
  } catch (err) {
    console.warn('API fetch failed, fallback to defaults:', err);
    renderApp();
  }
}

// Start application when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}
